import './libs/external/ppb-game-client-api-1.0.8';

type TResponseType =
  | 'balanceChanged'
  | 'settingsUpdate'
  | 'togglePaytable'
  | 'notifyAction'
  | 'receiveGameWindowConfig'
  | 'registerCallbackFunction'
  | 'setMessagingEventTarget'
  | 'stopAutoSpin'
  | 'launchGameDetails';

enum BETFAIR_RECEIVE_MESSAGE_TYPES {
  BALANCE_CHANGED = 'balanceChanged',
  SETTINGS_UPDATE = 'settingsUpdate',
  TOGGLE_PAYTABLE = 'togglePaytable',
  NOTIFY_ACTION = 'notifyAction',
  RECEIVE_GAME_WINDOW_CONFIG = 'receiveGameWindowConfig',
  REGISTER_CALLBACK_FUNCTION = 'registerCallbackFunction',
  SET_MESSAGING_EVENT_TARGET = 'setMessagingEventTarget',
  STOP_AUTO_SPIN = 'stopAutoSpin',
  LAUNCH_GAME_DETAILS = 'launchGameDetails',
}

enum EPlayMode {
  NORMAL = 'normal',
  AUTO_SPIN = 'autoSpin',
  FREE_SPIN = 'freeSpin',
  BONUS_ROUND = 'bonusRound',
}

class PpbClient {
  private currency = '';
  private freebets = 0;
  private autospin = false;
  private currentState?: EPlayMode;
  private observers: { [key: string]: ((...args: any) => void)[] } = {};
  private gc;
  private readonly gcRegisterCallbackFunction;

  constructor() {
    this.gc = (window as any).com.ppb.gc;
    this.gcRegisterCallbackFunction = this.gc.registerCallbackFunction;

    //ON BALANCE CHANGE
    this.gcRegisterCallbackFunction(
      BETFAIR_RECEIVE_MESSAGE_TYPES.BALANCE_CHANGED,
      () => {
        this.observers['balanceChanged']?.forEach(cb => cb());
      },
    );

    // //ON TOGGLE PAYTABLE
    this.gcRegisterCallbackFunction(
      BETFAIR_RECEIVE_MESSAGE_TYPES.TOGGLE_PAYTABLE,
      () => {
        this.observers['togglePaytable']?.forEach(cb => cb());
      },
    );

    // //ON STOP AUTOSPINS
    this.gcRegisterCallbackFunction(
      BETFAIR_RECEIVE_MESSAGE_TYPES.STOP_AUTO_SPIN,
      () => {
        this.observers['stopAutoSpin']?.forEach(cb => cb());
      },
    );

    //ON SETTINGS UPDATE
    this.gcRegisterCallbackFunction(
      BETFAIR_RECEIVE_MESSAGE_TYPES.SETTINGS_UPDATE,
      (payload: any) => {
        this.observers['settingsUpdate']?.forEach(cb => cb(payload));
      },
    );
  }

  sendPlayMode() {
    let currentState = EPlayMode.NORMAL;

    if (this.freebets) {
      currentState = EPlayMode.FREE_SPIN;
    } else if (this.autospin) {
      currentState = EPlayMode.AUTO_SPIN;
    }

    if (this.currentState === currentState) return;

    this.currentState = currentState;

    this.gc.sendPlayModeUpdate(currentState);
  }

  configure = (params: Record<string, string | boolean> = {}) => {
    for (const p in params) {
      if (this.hasOwnProperty(p)) {
        // @ts-ignore
        this[p] = params[p];
      }
    }
  };

  startLoad = () => {
    this.gc.sendGameLoadStarted();
  };

  setProgressLoad = (loadingPercent: number) => {
    this.gc.sendGameLoadProgress(loadingPercent);
    this.sendPlayMode();
  };

  endLoad = () => {
    this.gc.sendGameLoadCompleted();
  };

  startGame = () => {
    this.sendPlayMode();

    this.gc.sendGameAnimationStart();
    this.updateWin(0);
  };

  endGame = () => {
    this.gc.sendGameAnimationCompleted();
  };

  updateBalance = (balance: number) => {
    this.gc.sendBalanceUpdate(
      { realAmount: balance, bonusAmount: 0 },
      this.currency,
    );
  };

  updateBet = (bet: number, freebets?: number) => {
    this.gc.sendStakeUpdate(bet, this.currency);
    this.freebets = freebets || 0;
    this.sendPlayMode();
  };

  handleError = (error: { title: string; status: number; message: string }) => {
    const { title, message } = error;
    this.gc.sendErrorMessage(message, title);
  };

  updateWin = (win: number) => {
    this.gc.sendWinUpdate(win, this.currency);
  };

  updateSound = (active: boolean) => {
    this.gc.sendSettingsUpdate('sound', active ? 'on' : 'off');
  };

  toggleMenu = () => {
    // this.sendMessage("toggleMenu");
  };

  on = (type: TResponseType, callback: (...args: any) => void) => {
    if (!this.observers[type]) {
      this.observers[type] = [];
    }

    if (!this.observers[type].includes(callback)) {
      this.observers[type].push(callback);
    }
  };

  off = (type: TResponseType, callback: (...args: any) => void) => {
    if (!this.observers[type]) return;

    const observerIndex = this.observers[type].indexOf(callback);

    if (observerIndex > -1) {
      this.observers[type].splice(observerIndex, 1);
    }
  };

  setCurrency = (currency: string) => {
    this.currency = currency;
  }
}

const ppbClient = new PpbClient();

export default ppbClient;
